import AutoNumeric from "autonumeric";
import { uiState } from "../../state/uiState.js";
import { renderDemirbasGrid, clearDemirbasGrid } from "./demirbasGrid.js";
import { truncateLabel } from "../../utils/textPresentation.js";
import { bindAmountPasteGuard } from "./amountPasteGuard.js";

const apiBaseUrl = "/api";

let didBound = false;
const FILTER_AMOUNT_AUTONUMERIC_CONFIG = {
    currencySymbol: " \u20BA",
    currencySymbolPlacement: "s",
    decimalCharacter: ",",
    digitGroupSeparator: ".",
    decimalPlaces: 2,
    minimumValue: "0",
    maximumValue: "999999999",
    modifyValueOnWheel: false,
    emptyInputBehavior: "null",
};

function readAmountFilterValue(inputEl) {
    if (!inputEl?._an) return inputEl?.value?.trim?.() || "";
    return inputEl._an.getNumericString() || "";
}

function syncAmountFilterStateFromDom() {
    const satinAlmaMinInput = document.querySelector("#satin_alma_min_filter");
    const satinAlmaMaxInput = document.querySelector("#satin_alma_max_filter");
    uiState.demirbas.filters.satin_alma_min = readAmountFilterValue(satinAlmaMinInput);
    uiState.demirbas.filters.satin_alma_max = readAmountFilterValue(satinAlmaMaxInput);
}

function clearAmountFilterInput(inputEl) {
    if (!inputEl) return;
    if (inputEl._an) {
        inputEl._an.clear();
        return;
    }
    inputEl.value = "";
}

function syncAmountFilterInputFromState(inputEl, rawValue) {
    if (!inputEl) return;
    if (inputEl._an) {
        if (rawValue) inputEl._an.set(rawValue);
        else inputEl._an.clear();
        return;
    }
    inputEl.value = rawValue || "";
}

function bindAmountFilterInput(inputEl, stateKey) {
    if (!inputEl || inputEl.dataset.amountFilterBound === "true") return;
    inputEl.dataset.amountFilterBound = "true";
    const sync = () => {
        uiState.demirbas.filters[stateKey] = readAmountFilterValue(inputEl);
    };
    inputEl.addEventListener("input", sync);
    inputEl.addEventListener("blur", sync);
}

export function initDemirbasFilterAmountInputs() {
    const satinAlmaMinInput = document.querySelector("#satin_alma_min_filter");
    const satinAlmaMaxInput = document.querySelector("#satin_alma_max_filter");

    [
        [satinAlmaMinInput, "satin_alma_min"],
        [satinAlmaMaxInput, "satin_alma_max"],
    ].forEach(([inputEl, stateKey]) => {
        if (!inputEl) return;
        if (!inputEl?._an) {
            inputEl._an = new AutoNumeric(inputEl, FILTER_AMOUNT_AUTONUMERIC_CONFIG);
        }
        bindAmountPasteGuard(inputEl);
        bindAmountFilterInput(inputEl, stateKey);
        syncAmountFilterInputFromState(inputEl, uiState.demirbas.filters[stateKey]);
    });
}

export function destroyDemirbasFilterAmountInputs() {
    const inputs = [
        document.querySelector("#satin_alma_min_filter"),
        document.querySelector("#satin_alma_max_filter"),
    ];

    inputs.forEach((inputEl) => {
        if (!inputEl?._an) return;
        try {
            inputEl._an.remove();
        } catch {
            // ignore cleanup failure
        }
        delete inputEl._an;
    });
}

function resetSelectToPlaceholder(selectElement, placeholderText, { disabled } = { disabled: true }) {
    if (!selectElement) return;
    selectElement.innerHTML = "";
    const placeholderOption = document.createElement("option");
    placeholderOption.value = "";
    placeholderOption.textContent = placeholderText;
    placeholderOption.title = placeholderText;
    placeholderOption.selected = true;
    selectElement.appendChild(placeholderOption);
    selectElement.disabled = Boolean(disabled);
    selectElement.title = placeholderText;
}

function showFilterOverlay(container) {
    let ov = container.querySelector(".filter-loading-overlay");
    if (!ov) {
        ov = document.createElement("div");
        ov.className = "filter-loading-overlay";
        ov.innerHTML = `<div class="filter-loading-spinner"></div><span class="filter-loading-text">Yükleniyor...</span>`;
        container.appendChild(ov);
    }
    ov.classList.remove("hidden");
}

function hideFilterOverlay(container) {
    container.querySelector(".filter-loading-overlay")?.classList.add("hidden");
}

async function withFilterOverlay(container, fn) {
    showFilterOverlay(container);
    try { await fn(); }
    finally {
        await new Promise(r => setTimeout(r, 300));
        hideFilterOverlay(container);
    }
}

function showDropdownLoading(selectEl) {
    selectEl?.closest(".column-container")?.classList.add("loading");
}

function hideDropdownLoading(selectEl) {
    selectEl?.closest(".column-container")?.classList.remove("loading");
}

export function resetDemirbasFilters() {
    const departmanSelect = document.querySelector("#departman");
    const urunGrubuSelect = document.querySelector("#urun_grubu");
    const urunAltGrubuSelect = document.querySelector("#urun_alt_grubu");
    const urunCinsiSelect = document.querySelector("#urun_cinsi");
    const markaSelect = document.querySelector("#marka");
    const modelSelect = document.querySelector("#model");
    const faturaNoInput = document.querySelector("#fatura_no");
    const tarihBaslangicInput = document.querySelector("#tarih_baslangic");
    const tarihBitisInput = document.querySelector("#tarih_bitis");
    const urunDurumuSelect = document.querySelector("#urun_durumu_filter");
    const zimmetDurumuSelect = document.querySelector("#zimmet_durumu_filter");
    const zimmetlenenKisiSelect = document.querySelector("#zimmetlenen_kisi_filter");
    const lokasyonSelect = document.querySelector("#lokasyon_filter");
    const odaSelect = document.querySelector("#oda_filter");
    const yerSelect = document.querySelector("#yer_filter");
    const satinAlmaMinInput = document.querySelector("#satin_alma_min_filter");
    const satinAlmaMaxInput = document.querySelector("#satin_alma_max_filter");
    const badge = document.querySelector(".filter-aktif-badge");
    const countEl = document.getElementById("demirbas-count");

    if (departmanSelect) departmanSelect.value = "";
    resetSelectToPlaceholder(urunGrubuSelect, "Ürün grubu seçiniz", { disabled: true });
    resetSelectToPlaceholder(urunAltGrubuSelect, "Ürün alt grubu seçiniz", { disabled: true });
    resetSelectToPlaceholder(urunCinsiSelect, "Ürün cinsi seçiniz", { disabled: true });
    resetSelectToPlaceholder(markaSelect, "Marka seçiniz", { disabled: true });
    resetSelectToPlaceholder(modelSelect, "Model seçiniz", { disabled: true });
    if (faturaNoInput) faturaNoInput.value = "";
    if (tarihBaslangicInput) { tarihBaslangicInput.value = ""; tarihBaslangicInput.max = ""; }
    if (tarihBitisInput) { tarihBitisInput.value = ""; tarihBitisInput.min = ""; }
    if (urunDurumuSelect) urunDurumuSelect.value = "";
    if (zimmetDurumuSelect) zimmetDurumuSelect.value = "";
    if (zimmetlenenKisiSelect) zimmetlenenKisiSelect.value = "";
    if (lokasyonSelect) lokasyonSelect.value = "";
    resetSelectToPlaceholder(odaSelect, "Tümü", { disabled: true });
    resetSelectToPlaceholder(yerSelect, "Tümü", { disabled: true });
    clearAmountFilterInput(satinAlmaMinInput);
    clearAmountFilterInput(satinAlmaMaxInput);

    document.querySelectorAll("#filter-section .column-container.loading")
        .forEach((el) => el.classList.remove("loading"));

    Object.keys(uiState.demirbas.filters).forEach((key) => {
        uiState.demirbas.filters[key] = "";
    });

    clearDemirbasGrid();
    if (badge) badge.hidden = true;
    if (countEl) countEl.textContent = "";
}

export function initDemirbasFilters() {
    const sectionBody        = document.querySelector("#filter-section .section-body");
    const departmanSelect    = document.querySelector("#departman");
    const urunGrubuSelect    = document.querySelector("#urun_grubu");
    const urunAltGrubuSelect = document.querySelector("#urun_alt_grubu");
    const urunCinsiSelect    = document.querySelector("#urun_cinsi");
    const markaSelect        = document.querySelector("#marka");
    const modelSelect        = document.querySelector("#model");

    const faturaNoInput         = document.querySelector("#fatura_no");
    const tarihBaslangicInput   = document.querySelector("#tarih_baslangic");
    const tarihBitisInput       = document.querySelector("#tarih_bitis");
    const urunDurumuSelect      = document.querySelector("#urun_durumu_filter");
    const zimmetDurumuSelect    = document.querySelector("#zimmet_durumu_filter");
    const zimmetlenenKisiSelect = document.querySelector("#zimmetlenen_kisi_filter");

    const lokasyonSelect = document.querySelector("#lokasyon_filter");
    const odaSelect      = document.querySelector("#oda_filter");
    const yerSelect      = document.querySelector("#yer_filter");
    const satinAlmaMinInput = document.querySelector("#satin_alma_min_filter");
    const satinAlmaMaxInput = document.querySelector("#satin_alma_max_filter");

    initDemirbasFilterAmountInputs();

    function fillSelect(selectElement, placeholderText, items) {
        resetSelectToPlaceholder(selectElement, placeholderText, { disabled: false });
        items.forEach((item) => {
            const fullLabel = String(item.name ?? "");
            const option = document.createElement("option");
            option.value = String(item.id);
            option.textContent = truncateLabel(fullLabel, 32);
            option.title = fullLabel;
            selectElement.appendChild(option);
        });
        const selected = selectElement.options?.[selectElement.selectedIndex];
        selectElement.title = selected?.title?.trim?.() || selected?.textContent?.trim() || "";
    }

    async function fetchJson(path) {
        const response = await fetch(`${apiBaseUrl}${path}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
    }

    async function loadDepartmanlar() {
        resetSelectToPlaceholder(departmanSelect, "Departman seçiniz", { disabled: false });
        const data = await fetchJson("/departmanlar");
        fillSelect(departmanSelect, "Departman seçiniz", data.items);
    }

    async function loadSimpleDropdown(path, selectEl) {
        // İlk "Tümü" option dışındakileri temizle
        while (selectEl.options.length > 1) selectEl.remove(1);
        try {
            const data = await fetchJson(path);
            (data.items || []).forEach((item) => {
                const fullLabel = String(item.name || item.ad_soyad || "");
                const opt = document.createElement("option");
                opt.value = String(item.id ?? item.tc);
                opt.textContent = truncateLabel(fullLabel, 32);
                opt.title = fullLabel;
                selectEl.appendChild(opt);
            });
            const selected = selectEl.options?.[selectEl.selectedIndex];
            selectEl.title = selected?.title?.trim?.() || selected?.textContent?.trim() || "";
        } catch (err) {
            console.error(`❌ ${path} yüklenemedi:`, err);
        }
    }

    async function loadKisilerDropdown() {
        while (zimmetlenenKisiSelect.options.length > 1) zimmetlenenKisiSelect.remove(1);
        try {
            const data = await fetchJson("/kisiler-dropdown?durum=tumu");
            (data.items || []).forEach((item) => {
                const fullLabel = String(item.ad_soyad ?? "");
                const opt = document.createElement("option");
                opt.value = item.tc;
                opt.textContent = truncateLabel(fullLabel, 32);
                opt.title = fullLabel;
                zimmetlenenKisiSelect.appendChild(opt);
            });
            const selected = zimmetlenenKisiSelect.options?.[zimmetlenenKisiSelect.selectedIndex];
            zimmetlenenKisiSelect.title = selected?.title?.trim?.() || selected?.textContent?.trim() || "";
        } catch (err) {
            console.error("❌ /kisiler-dropdown yüklenemedi:", err);
        }
    }

    // Her sayfa ziyaretinde init dropdown'larını yeniden yükle
    if (sectionBody) {
        withFilterOverlay(sectionBody, () => Promise.all([
            loadDepartmanlar(),
            loadSimpleDropdown("/urun-durumlari", urunDurumuSelect),
            loadSimpleDropdown("/zimmet-durumlari", zimmetDurumuSelect),
            loadKisilerDropdown(),
            loadSimpleDropdown("/lokasyonlar", lokasyonSelect),
        ])).catch((err) => console.error("❌ Dropdown init hatası:", err));
    } else {
        Promise.all([
            loadDepartmanlar(),
            loadSimpleDropdown("/urun-durumlari", urunDurumuSelect),
            loadSimpleDropdown("/zimmet-durumlari", zimmetDurumuSelect),
            loadKisilerDropdown(),
            loadSimpleDropdown("/lokasyonlar", lokasyonSelect),
        ]).catch((err) => console.error("❌ Dropdown init hatası:", err));
    }

    if (didBound) return;
    didBound = true;

    // ── Cascading: Departman → Model ──
    departmanSelect?.addEventListener("change", async () => {
        const departmanId = departmanSelect.value;
        resetSelectToPlaceholder(urunGrubuSelect, "Ürün grubu seçiniz", { disabled: true });
        resetSelectToPlaceholder(urunAltGrubuSelect, "Ürün alt grubu seçiniz", { disabled: true });
        resetSelectToPlaceholder(urunCinsiSelect, "Ürün cinsi seçiniz", { disabled: true });
        resetSelectToPlaceholder(markaSelect, "Marka seçiniz", { disabled: true });
        resetSelectToPlaceholder(modelSelect, "Model seçiniz", { disabled: true });
        uiState.demirbas.filters.departman_id = departmanId || "";
        uiState.demirbas.filters.departman_urun_grubu_id = "";
        uiState.demirbas.filters.urun_grubu_urun_alt_grubu_id = "";
        uiState.demirbas.filters.urun_alt_grubu_urun_cinsi_id = "";
        uiState.demirbas.filters.urun_cinsi_marka_id = "";
        uiState.demirbas.filters.marka_model_id = "";
        if (!departmanId) return;
        showDropdownLoading(urunGrubuSelect);
        try {
            const data = await fetchJson(`/urun-gruplari?departman_id=${departmanId}`);
            fillSelect(urunGrubuSelect, "Ürün grubu seçiniz", data.items);
        } finally { hideDropdownLoading(urunGrubuSelect); }
    });

    urunGrubuSelect?.addEventListener("change", async () => {
        const id = urunGrubuSelect.value;
        resetSelectToPlaceholder(urunAltGrubuSelect, "Ürün alt grubu seçiniz", { disabled: true });
        resetSelectToPlaceholder(urunCinsiSelect, "Ürün cinsi seçiniz", { disabled: true });
        resetSelectToPlaceholder(markaSelect, "Marka seçiniz", { disabled: true });
        resetSelectToPlaceholder(modelSelect, "Model seçiniz", { disabled: true });
        uiState.demirbas.filters.departman_urun_grubu_id = id || "";
        uiState.demirbas.filters.urun_grubu_urun_alt_grubu_id = "";
        uiState.demirbas.filters.urun_alt_grubu_urun_cinsi_id = "";
        uiState.demirbas.filters.urun_cinsi_marka_id = "";
        uiState.demirbas.filters.marka_model_id = "";
        if (!id) return;
        showDropdownLoading(urunAltGrubuSelect);
        try {
            const data = await fetchJson(`/urun-alt-gruplari?departman_urun_grubu_id=${id}`);
            fillSelect(urunAltGrubuSelect, "Ürün alt grubu seçiniz", data.items);
        } finally { hideDropdownLoading(urunAltGrubuSelect); }
    });

    urunAltGrubuSelect?.addEventListener("change", async () => {
        const id = urunAltGrubuSelect.value;
        resetSelectToPlaceholder(urunCinsiSelect, "Ürün cinsi seçiniz", { disabled: true });
        resetSelectToPlaceholder(markaSelect, "Marka seçiniz", { disabled: true });
        resetSelectToPlaceholder(modelSelect, "Model seçiniz", { disabled: true });
        uiState.demirbas.filters.urun_grubu_urun_alt_grubu_id = id || "";
        uiState.demirbas.filters.urun_alt_grubu_urun_cinsi_id = "";
        uiState.demirbas.filters.urun_cinsi_marka_id = "";
        uiState.demirbas.filters.marka_model_id = "";
        if (!id) return;
        showDropdownLoading(urunCinsiSelect);
        try {
            const data = await fetchJson(`/urun-cinsleri?urun_grubu_urun_alt_grubu_id=${id}`);
            fillSelect(urunCinsiSelect, "Ürün cinsi seçiniz", data.items);
        } finally { hideDropdownLoading(urunCinsiSelect); }
    });

    urunCinsiSelect?.addEventListener("change", async () => {
        const id = urunCinsiSelect.value;
        resetSelectToPlaceholder(markaSelect, "Marka seçiniz", { disabled: true });
        resetSelectToPlaceholder(modelSelect, "Model seçiniz", { disabled: true });
        uiState.demirbas.filters.urun_alt_grubu_urun_cinsi_id = id || "";
        uiState.demirbas.filters.urun_cinsi_marka_id = "";
        uiState.demirbas.filters.marka_model_id = "";
        if (!id) return;
        showDropdownLoading(markaSelect);
        try {
            const data = await fetchJson(`/markalar?urun_alt_grubu_urun_cinsi_id=${id}`);
            fillSelect(markaSelect, "Marka seçiniz", data.items);
        } finally { hideDropdownLoading(markaSelect); }
    });

    markaSelect?.addEventListener("change", async () => {
        const id = markaSelect.value;
        resetSelectToPlaceholder(modelSelect, "Model seçiniz", { disabled: true });
        uiState.demirbas.filters.urun_cinsi_marka_id = id || "";
        uiState.demirbas.filters.marka_model_id = "";
        if (!id) return;
        showDropdownLoading(modelSelect);
        try {
            const data = await fetchJson(`/modeller?urun_cinsi_marka_id=${id}`);
            fillSelect(modelSelect, "Model seçiniz", data.items);
        } finally { hideDropdownLoading(modelSelect); }
    });

    modelSelect?.addEventListener("change", () => {
        uiState.demirbas.filters.marka_model_id = modelSelect.value || "";
    });

    // ── Cascading: Lokasyon → Oda → Yer ──
    lokasyonSelect?.addEventListener("change", async () => {
        resetSelectToPlaceholder(odaSelect, "Tümü", { disabled: true });
        resetSelectToPlaceholder(yerSelect, "Tümü", { disabled: true });
        uiState.demirbas.filters.lokasyon_id     = lokasyonSelect.value;
        uiState.demirbas.filters.lokasyon_oda_id = "";
        uiState.demirbas.filters.oda_yer_id      = "";
        if (!lokasyonSelect.value) return;
        showDropdownLoading(odaSelect);
        try {
            const data = await fetchJson(`/odalar-by-lokasyon?lokasyon_id=${lokasyonSelect.value}`);
            fillSelect(odaSelect, "Tümü", data.items);
        } finally { hideDropdownLoading(odaSelect); }
    });

    odaSelect?.addEventListener("change", async () => {
        resetSelectToPlaceholder(yerSelect, "Tümü", { disabled: true });
        uiState.demirbas.filters.lokasyon_oda_id = odaSelect.value;
        uiState.demirbas.filters.oda_yer_id      = "";
        if (!odaSelect.value) return;
        showDropdownLoading(yerSelect);
        try {
            const data = await fetchJson(`/yerler-by-oda?lokasyon_oda_id=${odaSelect.value}`);
            fillSelect(yerSelect, "Tümü", data.items);
        } finally { hideDropdownLoading(yerSelect); }
    });

    yerSelect?.addEventListener("change", () => {
        uiState.demirbas.filters.oda_yer_id = yerSelect.value;
    });

    // ── Yeni filtre input'ları ──
    faturaNoInput?.addEventListener("input", () => {
        uiState.demirbas.filters.fatura_no = faturaNoInput.value.trim();
    });

    tarihBaslangicInput?.addEventListener("change", () => {
        if (tarihBitisInput?.value && tarihBaslangicInput.value > tarihBitisInput.value) {
            tarihBaslangicInput.value = "";
            uiState.demirbas.filters.tarih_baslangic = "";
            return;
        }
        uiState.demirbas.filters.tarih_baslangic = tarihBaslangicInput.value;
        if (tarihBitisInput) tarihBitisInput.min = tarihBaslangicInput.value || "";
    });

    tarihBitisInput?.addEventListener("change", () => {
        if (tarihBaslangicInput?.value && tarihBitisInput.value < tarihBaslangicInput.value) {
            tarihBitisInput.value = "";
            uiState.demirbas.filters.tarih_bitis = "";
            return;
        }
        uiState.demirbas.filters.tarih_bitis = tarihBitisInput.value;
        if (tarihBaslangicInput) tarihBaslangicInput.max = tarihBitisInput.value || "";
    });

    urunDurumuSelect?.addEventListener("change", () => {
        uiState.demirbas.filters.urun_durumu_id = urunDurumuSelect.value;
    });
    zimmetDurumuSelect?.addEventListener("change", () => {
        uiState.demirbas.filters.zimmet_durumu_id = zimmetDurumuSelect.value;
    });
    zimmetlenenKisiSelect?.addEventListener("change", () => {
        uiState.demirbas.filters.zimmetlenen_tc = zimmetlenenKisiSelect.value;
    });

    // ── Ara / Temizle ──
    const searchButton = document.querySelector(".search");
    const resetButton  = document.querySelector(".filter-reset");

    function buildQueryString(filters) {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value) params.append(key, value);
        });
        return params.toString();
    }

    searchButton?.addEventListener("click", () => {
        uiState.demirbas.searchMode = "filtre";
        uiState.demirbas.barcodeQuery = "";
        syncAmountFilterStateFromDom();
        const queryString = buildQueryString(uiState.demirbas.filters);
        const url = queryString ? `/api/demirbas?${queryString}` : "/api/demirbas";
        renderDemirbasGrid({ url, paginationLimit: uiState.demirbas.paginationLimit });
        const badge = document.querySelector(".filter-aktif-badge");
        if (badge) badge.hidden = !queryString;
    });

    resetButton?.addEventListener("click", () => {
        resetDemirbasFilters();
        if (departmanSelect) departmanSelect.value = "";
        resetSelectToPlaceholder(urunGrubuSelect, "Ürün grubu seçiniz", { disabled: true });
        resetSelectToPlaceholder(urunAltGrubuSelect, "Ürün alt grubu seçiniz", { disabled: true });
        resetSelectToPlaceholder(urunCinsiSelect, "Ürün cinsi seçiniz", { disabled: true });
        resetSelectToPlaceholder(markaSelect, "Marka seçiniz", { disabled: true });
        resetSelectToPlaceholder(modelSelect, "Model seçiniz", { disabled: true });
        if (faturaNoInput)       faturaNoInput.value = "";
        if (tarihBaslangicInput) { tarihBaslangicInput.value = ""; tarihBaslangicInput.max = ""; }
        if (tarihBitisInput)     { tarihBitisInput.value = "";     tarihBitisInput.min = ""; }
        if (urunDurumuSelect)    urunDurumuSelect.value = "";
        if (zimmetDurumuSelect)  zimmetDurumuSelect.value = "";
        if (zimmetlenenKisiSelect) zimmetlenenKisiSelect.value = "";
        if (lokasyonSelect) lokasyonSelect.value = "";
        resetSelectToPlaceholder(odaSelect, "Tümü", { disabled: true });
        resetSelectToPlaceholder(yerSelect, "Tümü", { disabled: true });
        clearAmountFilterInput(satinAlmaMinInput);
        clearAmountFilterInput(satinAlmaMaxInput);

        Object.keys(uiState.demirbas.filters).forEach((k) => (uiState.demirbas.filters[k] = ""));

        clearDemirbasGrid();
        const badge = document.querySelector(".filter-aktif-badge");
        if (badge) badge.hidden = true;
    });
}
